import { useMutation, useQuery } from "@apollo/client";
import PhoneDisabledOutlinedIcon from "@mui/icons-material/PhoneDisabledOutlined";
import PhoneEnabledOutlinedIcon from "@mui/icons-material/PhoneEnabledOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import { useCanAccess, useNotify, useTranslate } from "react-admin";

import { TELEPHONY_RESOURCE } from "../../constants/resources";
import { BeginPlacetelPhoneSignInDocument, PlacetelPhoneAccountDocument } from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { phoneSignInRedirectUri, rememberPhoneSignInReturn } from "../../lib/placetelPhoneSignIn";
import { space } from "../../theme/designTokens";
import { usePhone } from "./PhoneProvider";

/** Header control: activates the portal phone once, then opens and closes the phone panel. */
export const PhoneActivation = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { canAccess } = useCanAccess({ action: "list", resource: TELEPHONY_RESOURCE });
  const { data, loading, error } = useQuery(PlacetelPhoneAccountDocument, { skip: !canAccess });
  const [beginSignIn, { loading: starting }] = useMutation(BeginPlacetelPhoneSignInDocument);
  const { displayStatus, activeCall, panelOpen, openPanel, closePanel } = usePhone();

  const account = data?.placetelPhoneAccount;
  if (!canAccess || loading || error || !account?.available) return null;

  const activate = async () => {
    try {
      rememberPhoneSignInReturn();
      const result = await beginSignIn({ variables: { redirectUri: phoneSignInRedirectUri() } });
      const authorizationUrl = result.data?.beginPlacetelPhoneSignIn.authorizationUrl;
      if (!authorizationUrl) throw new Error("Phone sign-in did not return an authorization URL");
      window.location.assign(authorizationUrl);
    } catch (activateError) {
      captureException(activateError, "telephony/phone-sign-in: begin");
      notify("telephony.activateError", { type: "error" });
    }
  };

  if (!account.connected) {
    return (
      <Button
        startIcon={<PhoneOutlinedIcon />}
        onClick={activate}
        disabled={starting}
        data-testid="phone-activate-button"
        aria-label={translate("telephony.activate")}
        title={translate("telephony.activate")}
        sx={{
          minWidth: 44,
          minHeight: 44,
          px: { xs: `${space.sm}px`, sm: `${space.xl}px` },
          "& .MuiButton-startIcon": {
            ml: { xs: 0, sm: `-${space.xs}px` },
            mr: { xs: 0, sm: `${space.sm}px` },
          },
        }}
      >
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
          {translate("telephony.activate")}
        </Box>
      </Button>
    );
  }

  const isStarting = displayStatus === "starting";
  const inCall = activeCall !== null;
  let PhoneIcon = PhoneEnabledOutlinedIcon;
  if (inCall) PhoneIcon = PhoneInTalkOutlinedIcon;
  else if (displayStatus === "error" || displayStatus === "unavailable") PhoneIcon = PhoneDisabledOutlinedIcon;

  return (
    <IconButton
      data-testid="phone-panel-button"
      aria-label={translate("telephony.panel.title")}
      title={translate("telephony.panel.title")}
      aria-pressed={panelOpen}
      onClick={panelOpen ? closePanel : openPanel}
      sx={{ minWidth: 44, minHeight: 44 }}
    >
      <Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
        <PhoneIcon
          color={inCall ? "success" : "inherit"}
          data-testid={inCall ? "phone-in-call-icon" : undefined}
          sx={{ opacity: isStarting ? 0.5 : 1 }}
        />
        {isStarting && (
          <CircularProgress
            size={16}
            data-testid="phone-status-progress"
            sx={{ position: "absolute", top: "50%", left: "50%", mt: "-8px", ml: "-8px" }}
          />
        )}
      </Box>
    </IconButton>
  );
};
